import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { KATEGORIER } from '../components/CategorySelector';
import { FYLKER_MED_BYER, getFylkeName, getByName, getByFromCoords } from '../data/norwegianCities';
import useFormPersistence from '../hooks/useFormPersistence';
import { isDemoMode } from '../demo/useDemoMode';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const CreateJobPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [currentStep, setCurrentStep] = useState(0); // Start at step 0 for type selection
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [locating, setLocating] = useState(false);
  
  // Bruk localStorage for å bevare data
  const [formData, setFormData, clearFormData] = useFormPersistence('create_job', {
    job_type: '', // 'oppdrag' eller 'tjeneste'
    title: '',
    description: '',
    category: '',
    location_fylke: user?.location_fylke || '',
    location_city: user?.location_city || '',
    price: '',
    pricing_type: 'fixed', // 'fixed', 'hourly', 'negotiable'
    hourly_rate: ''
  });

  const [images, setImages] = useState([]);
  const [primaryImageIndex, setPrimaryImageIndex] = useState(0);

  // Dynamiske steg basert på job_type
  const getSteps = () => {
    const baseSteps = [
      { id: 0, title: 'Velg type', icon: 'category' }
    ];

    if (formData.job_type === 'oppdrag') {
      return [
        ...baseSteps,
        { id: 1, title: 'Kategori', icon: 'label' },
        { id: 2, title: 'Detaljer', icon: 'edit_note' },
        { id: 3, title: 'Lokasjon & Pris', icon: 'location_on' },
        { id: 4, title: 'Bilder', icon: 'photo_library' },
        { id: 5, title: 'Publiser', icon: 'publish' },
      ];
    } else if (formData.job_type === 'tjeneste') {
      return [
        ...baseSteps,
        { id: 1, title: 'Kategori', icon: 'label' },
        { id: 2, title: 'Din tjeneste', icon: 'handyman' },
        { id: 3, title: 'Prising', icon: 'payments' },
        { id: 4, title: 'Område', icon: 'map' },
        { id: 5, title: 'Bilder', icon: 'photo_library' },
        { id: 6, title: 'Publiser', icon: 'publish' },
      ];
    }
    return baseSteps;
  };

  const STEPS = getSteps();
  const maxStep = STEPS.length - 1;

  // Hent byer for valgt fylke
  const currentFylke = formData.location_fylke 
    ? FYLKER_MED_BYER.find(f => f.id === formData.location_fylke) 
    : null;
  const byer = currentFylke?.byer || [];

  // Check age requirement (20 years) - this will be validated on server too
  // Skip in demo mode
  // NOTE: This hook must be called before any conditional returns to follow Rules of Hooks
  useEffect(() => {
    if (isDemoMode()) return; // Skip age check in demo

    const checkAgeRequirement = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API_URL}/users/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const userData = response.data;
        
        if (userData.birth_year) {
          const currentYear = new Date().getFullYear();
          const age = currentYear - userData.birth_year;
          if (age < 20) {
            setError('Du må være minst 20 år for å legge ut oppdrag.');
            setTimeout(() => navigate('/'), 3000);
          }
        }
      } catch (error) {
        // Silently fail - server will validate anyway
      }
    };
    
    if (isAuthenticated) {
      checkAgeRequirement();
    }
  }, [isAuthenticated, navigate]);

  // Redirect if not logged in
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center">
          <span className="material-symbols-outlined text-6xl text-slate-300 mb-4 block">
            lock
          </span>
          <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-2">
            Du må være logget inn
          </h2>
          <p className="text-slate-500 mb-4">
            Logg inn eller opprett en konto for å legge ut oppdrag.
          </p>
          <Link 
            to="/logg-inn"
            className="inline-block bg-[#0A1929] text-white px-6 py-3 rounded-lg font-medium hover:bg-[#1E3A5F] transition-colors"
          >
            Logg inn
          </Link>
        </div>
      </div>
    );
  }

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ [name]: value });
    setError('');
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    const newImages = files.slice(0, 5 - images.length);
    
    newImages.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImages(prev => [...prev, { file, preview: reader.result }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index) => {
    setImages(prev => prev.filter((_, i) => i !== index));
    if (primaryImageIndex === index) {
      setPrimaryImageIndex(0);
    } else if (primaryImageIndex > index) {
      setPrimaryImageIndex(prev => prev - 1);
    }
  };

  const handleUseLocation = () => {
    setLocating(true);
    
    if (!navigator.geolocation) {
      fetchIPLocation();
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        const result = getByFromCoords(latitude, longitude);
        if (result) {
          setFormData({
            location_fylke: result.fylkeId,
            location_city: result.byId
          });
        }
        setLocating(false);
      },
      () => fetchIPLocation(),
      { timeout: 5000 }
    );
  };

  const fetchIPLocation = async () => {
    try {
      const response = await fetch('https://ipapi.co/json/');
      const data = await response.json();
      if (data.latitude && data.longitude) {
        const result = getByFromCoords(data.latitude, data.longitude);
        if (result) {
          setFormData({
            location_fylke: result.fylkeId,
            location_city: result.byId
          });
        }
      }
    } catch (err) {
      console.error('IP location error:', err);
    } finally {
      setLocating(false);
    }
  };

  const validateStep = (step) => {
    setError('');
    
    if (step === 0) {
      if (!formData.job_type) {
        setError('Velg om du vil legge ut et oppdrag eller en tjeneste');
        return false;
      }
      return true;
    }

    if (formData.job_type === 'oppdrag') {
      switch (step) {
        case 1:
          if (!formData.category) {
            setError('Velg en kategori');
            return false;
          }
          break;
        case 2:
          if (!formData.title.trim()) {
            setError('Skriv inn en tittel');
            return false;
          }
          if (!formData.description.trim()) {
            setError('Skriv inn en beskrivelse');
            return false;
          }
          break;
        case 3:
          if (!formData.location_fylke) {
            setError('Velg et fylke');
            return false;
          }
          if (byer.length > 1 && !formData.location_city) {
            setError('Velg en by');
            return false;
          }
          const price = parseInt(formData.price);
          if (isNaN(price) || price < 0) {
            setError('Skriv inn en gyldig pris');
            return false;
          }
          break;
        default:
          break;
      }
    } else if (formData.job_type === 'tjeneste') {
      switch (step) {
        case 1:
          if (!formData.category) {
            setError('Velg en kategori');
            return false;
          }
          break;
        case 2:
          if (!formData.title.trim()) {
            setError('Gi tjenesten din en tittel');
            return false;
          }
          if (!formData.description.trim()) {
            setError('Beskriv hva du tilbyr');
            return false;
          }
          break;
        case 3:
          if (!formData.pricing_type) {
            setError('Velg en pristype');
            return false;
          }
          if (formData.pricing_type === 'fixed') {
            const price = parseInt(formData.price);
            if (isNaN(price) || price <= 0) {
              setError('Skriv inn en gyldig pris');
              return false;
            }
          } else if (formData.pricing_type === 'hourly') {
            const rate = parseInt(formData.hourly_rate);
            if (isNaN(rate) || rate <= 0) {
              setError('Skriv inn en gyldig timepris');
              return false;
            }
          }
          break;
        case 4:
          if (!formData.location_fylke) {
            setError('Velg et fylke');
            return false;
          }
          break;
        default:
          break;
      }
    }
    return true;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, maxStep));
    }
  };

  const prevStep = () => {
    setError('');
    setCurrentStep(prev => Math.max(prev - 1, 0));
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    // In demo mode, simulate success and redirect to first mock job
    if (isDemoMode()) {
      console.log('[DEMO] Mock job creation:', formData);
      setTimeout(() => {
        clearFormData();
        // Show alert that this is a demo
        alert('Demo: Annonsen ble "opprettet"! I den virkelige appen ville annonsen bli lagret og publisert.');
        navigate('/annonse/1'); // Redirect to first mock job
      }, 1000);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      const jobData = new FormData();
      jobData.append('title', formData.title);
      jobData.append('description', formData.description);
      jobData.append('category', formData.category);
      jobData.append('location_fylke', formData.location_fylke);
      jobData.append('location_city', formData.location_city || (byer.length === 1 ? byer[0].id : ''));
      jobData.append('job_type', formData.job_type);
      jobData.append('pricing_type', formData.pricing_type);
      
      if (formData.pricing_type === 'hourly') {
        jobData.append('hourly_rate', parseInt(formData.hourly_rate));
        jobData.append('price', 0);
      } else {
        jobData.append('price', parseInt(formData.price) || 0);
      }
      
      jobData.append('primaryImageIndex', primaryImageIndex);
      
      images.forEach((img) => {
        jobData.append('images', img.file);
      });

      const response = await axios.post(`${API_URL}/jobs`, jobData, {
        headers: { 
          'Authorization': `Bearer ${token}`
        }
      });

      clearFormData(); // Fjern lagret data ved suksess
      navigate(`/annonse/${response.data.id}`);
    } catch (error) {
      console.error('Feil ved opprettelse av annonse:', error);
      setError(error.response?.data?.message || 'Kunne ikke opprette annonsen. Prøv igjen.');
    } finally {
      setLoading(false);
    }
  };

  const getCategoryName = (id) => KATEGORIER.find(k => k.id === id)?.name || id;

  const renderStepContent = () => {
    // Step 0: Velg type (oppdrag eller tjeneste)
    if (currentStep === 0) {
      return (
        <div className="space-y-6">
          <div className="text-center mb-6">
            <h2 className="font-heading font-bold text-2xl text-[#0A1929] mb-2">
              Hva vil du lage?
            </h2>
            <p className="text-slate-500">
              Velg om du vil legge ut et oppdrag eller tilby en tjeneste
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {/* Oppdrag */}
            <button
              type="button"
              onClick={() => setFormData({ job_type: 'oppdrag' })}
              className={`p-6 rounded-xl border-2 transition-all text-left ${
                formData.job_type === 'oppdrag'
                  ? 'border-[#0A1929] bg-[#0A1929]/5'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                  formData.job_type === 'oppdrag' ? 'bg-[#0A1929] text-white' : 'bg-slate-100 text-slate-500'
                }`}>
                  <span className="material-symbols-outlined text-3xl">assignment</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-semibold text-lg text-[#0A1929] mb-1">
                    Legg ut et oppdrag
                  </h3>
                  <p className="text-slate-500 text-sm">
                    Du trenger hjelp med noe og vil betale noen for å gjøre det. 
                    F.eks. plenklipping, flyttehjelp, eller annet.
                  </p>
                </div>
              </div>
            </button>

            {/* Tjeneste */}
            <button
              type="button"
              onClick={() => setFormData({ job_type: 'tjeneste' })}
              className={`p-6 rounded-xl border-2 transition-all text-left ${
                formData.job_type === 'tjeneste'
                  ? 'border-[#0A1929] bg-[#0A1929]/5'
                  : 'border-slate-200 hover:border-slate-300'
              }`}
            >
              <div className="flex items-start gap-4">
                <div className={`w-14 h-14 rounded-xl flex items-center justify-center ${
                  formData.job_type === 'tjeneste' ? 'bg-[#0A1929] text-white' : 'bg-slate-100 text-slate-500'
                }`}>
                  <span className="material-symbols-outlined text-3xl">handyman</span>
                </div>
                <div className="flex-1">
                  <h3 className="font-heading font-semibold text-lg text-[#0A1929] mb-1">
                    Tilby en tjeneste
                  </h3>
                  <p className="text-slate-500 text-sm">
                    Du har ferdigheter og vil tilby dine tjenester til andre. 
                    F.eks. fotografering, hundelufting, eller datahjelp.
                  </p>
                  <p className="text-amber-600 text-xs mt-2 flex items-center gap-1">
                    <span className="material-symbols-outlined text-sm">info</span>
                    Du må være 16 år eller eldre for å tilby tjenester
                  </p>
                </div>
              </div>
            </button>
          </div>
        </div>
      );
    }

    // Oppdrag flow
    if (formData.job_type === 'oppdrag') {
      switch (currentStep) {
        case 1:
          return (
            <div className="space-y-4">
              <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-2">
                Hva slags oppdrag er dette?
              </h2>
              <p className="text-slate-500 text-sm mb-6">
                Velg den kategorien som passer best
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {KATEGORIER.map((kategori) => (
                  <button
                    key={kategori.id}
                    type="button"
                    onClick={() => setFormData({ category: kategori.id })}
                    className={`flex flex-col items-center gap-3 p-4 rounded-xl transition-all ${
                      formData.category === kategori.id
                        ? 'bg-[#0A1929] text-white shadow-lg scale-105'
                        : 'bg-white text-[#0A1929] border border-slate-200 hover:border-[#1E3A5F] hover:shadow-md'
                    }`}
                  >
                    <span className="material-symbols-outlined text-3xl">{kategori.icon}</span>
                    <span className="text-sm font-medium text-center">{kategori.name}</span>
                  </button>
                ))}
              </div>
            </div>
          );

        case 2:
          return (
            <div className="space-y-6">
              <div>
                <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-2">
                  Beskriv oppdraget
                </h2>
                <p className="text-slate-500 text-sm mb-6">
                  Gi en god tittel og beskrivelse så folk forstår hva du trenger hjelp med
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#0A1929] mb-2">
                  Tittel *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  maxLength={100}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent"
                  placeholder="F.eks. Trenger hjelp med hagearbeid"
                />
                <p className="text-xs text-slate-400 mt-1">{formData.title.length}/100</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[#0A1929] mb-2">
                  Beskrivelse *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={5}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent resize-none"
                  placeholder="Beskriv oppdraget i detalj. Inkluder informasjon om hva som skal gjøres, tidsramme, evt. utstyr som trengs osv."
                />
              </div>
            </div>
          );

        case 3:
          return (
            <div className="space-y-6">
              <div>
                <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-2">
                  Hvor og hvor mye?
                </h2>
                <p className="text-slate-500 text-sm mb-6">
                  Velg lokasjon og sett en pris for oppdraget
                </p>
              </div>

              {/* Bruk min posisjon */}
              <button
                type="button"
                onClick={handleUseLocation}
                disabled={locating}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  locating
                    ? 'bg-slate-100 text-slate-400'
                    : 'bg-gradient-to-r from-[#1E3A5F] to-[#0A1929] text-white shadow-md hover:shadow-lg'
                }`}
              >
                <span className={`material-symbols-outlined ${locating ? 'animate-pulse' : ''}`}>
                  {locating ? 'location_searching' : 'my_location'}
                </span>
                {locating ? 'Finner posisjon...' : 'Bruk min posisjon'}
              </button>

              {/* Fylke */}
              <div>
                <label className="block text-sm font-medium text-[#0A1929] mb-2">
                  Fylke *
                </label>
                <select
                  name="location_fylke"
                  value={formData.location_fylke}
                  onChange={(e) => setFormData({ location_fylke: e.target.value, location_city: '' })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent appearance-none bg-white"
                >
                  <option value="">Velg fylke</option>
                  {FYLKER_MED_BYER.map(fylke => (
                    <option key={fylke.id} value={fylke.id}>{fylke.name}</option>
                  ))}
                </select>
              </div>

              {/* By */}
              {formData.location_fylke && byer.length > 1 && (
                <div>
                  <label className="block text-sm font-medium text-[#0A1929] mb-2">
                    By *
                  </label>
                  <select
                    name="location_city"
                    value={formData.location_city}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent appearance-none bg-white"
                  >
                    <option value="">Velg by</option>
                    {byer.map(by => (
                      <option key={by.id} value={by.id}>{by.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Pris */}
              <div>
                <label className="block text-sm font-medium text-[#0A1929] mb-2">
                  Pris (NOK) *
                </label>
                <div className="relative">
                  <input
                    type="number"
                    name="price"
                    value={formData.price}
                    onChange={handleChange}
                    min="0"
                    step="50"
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent"
                    placeholder="0"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium">
                    kr
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Hvor mye er du villig til å betale for oppdraget?
                </p>
              </div>
            </div>
          );

        case 4:
          return renderImageStep();

        case 5:
          return renderPreviewStep();

        default:
          return null;
      }
    }

    // Tjeneste flow
    if (formData.job_type === 'tjeneste') {
      switch (currentStep) {
        case 1:
          return (
            <div className="space-y-4">
              <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-2">
                Hva slags tjeneste tilbyr du?
              </h2>
              <p className="text-slate-500 text-sm mb-6">
                Velg den kategorien som passer best
              </p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {KATEGORIER.map((kategori) => (
                  <button
                    key={kategori.id}
                    type="button"
                    onClick={() => setFormData({ category: kategori.id })}
                    className={`flex flex-col items-center gap-3 p-4 rounded-xl transition-all ${
                      formData.category === kategori.id
                        ? 'bg-[#0A1929] text-white shadow-lg scale-105'
                        : 'bg-white text-[#0A1929] border border-slate-200 hover:border-[#1E3A5F] hover:shadow-md'
                    }`}
                  >
                    <span className="material-symbols-outlined text-3xl">{kategori.icon}</span>
                    <span className="text-sm font-medium text-center">{kategori.name}</span>
                  </button>
                ))}
              </div>
            </div>
          );

        case 2:
          return (
            <div className="space-y-6">
              <div>
                <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-2">
                  Beskriv din tjeneste
                </h2>
                <p className="text-slate-500 text-sm mb-6">
                  Fortell hva du kan hjelpe med og hva som gjør deg bra på det
                </p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-[#0A1929] mb-2">
                  Tittel på tjenesten *
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  maxLength={100}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent"
                  placeholder="F.eks. Profesjonell hundelufting"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#0A1929] mb-2">
                  Beskrivelse *
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows={5}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent resize-none"
                  placeholder="Beskriv hva du tilbyr, din erfaring, og hva kunden kan forvente."
                />
              </div>
            </div>
          );

        case 3:
          return (
            <div className="space-y-6">
              <div>
                <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-2">
                  Hva tar du betalt?
                </h2>
                <p className="text-slate-500 text-sm mb-6">
                  Velg hvordan du vil prise tjenesten din
                </p>
              </div>

              {/* Pristype valg */}
              <div className="grid grid-cols-1 gap-3">
                <button
                  type="button"
                  onClick={() => setFormData({ pricing_type: 'fixed' })}
                  className={`p-4 rounded-xl border-2 transition-all text-left flex items-center gap-4 ${
                    formData.pricing_type === 'fixed'
                      ? 'border-[#0A1929] bg-[#0A1929]/5'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <span className="material-symbols-outlined text-2xl text-[#1E3A5F]">sell</span>
                  <div>
                    <span className="font-medium text-[#0A1929] block">Fast pris (akkord)</span>
                    <span className="text-sm text-slate-500">En fast sum for hele jobben</span>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setFormData({ pricing_type: 'hourly' })}
                  className={`p-4 rounded-xl border-2 transition-all text-left flex items-center gap-4 ${
                    formData.pricing_type === 'hourly'
                      ? 'border-[#0A1929] bg-[#0A1929]/5'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <span className="material-symbols-outlined text-2xl text-[#1E3A5F]">schedule</span>
                  <div>
                    <span className="font-medium text-[#0A1929] block">Timepris</span>
                    <span className="text-sm text-slate-500">Betaling per time</span>
                  </div>
                </button>

                <button
                  type="button"
                  onClick={() => setFormData({ pricing_type: 'negotiable' })}
                  className={`p-4 rounded-xl border-2 transition-all text-left flex items-center gap-4 ${
                    formData.pricing_type === 'negotiable'
                      ? 'border-[#0A1929] bg-[#0A1929]/5'
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <span className="material-symbols-outlined text-2xl text-[#1E3A5F]">handshake</span>
                  <div>
                    <span className="font-medium text-[#0A1929] block">Etter avtale</span>
                    <span className="text-sm text-slate-500">Pris avtales med kunden</span>
                  </div>
                </button>
              </div>

              {/* Pris input basert på type */}
              {formData.pricing_type === 'fixed' && (
                <div>
                  <label className="block text-sm font-medium text-[#0A1929] mb-2">
                    Fast pris (NOK) *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleChange}
                      min="0"
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent"
                      placeholder="0"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium">
                      kr
                    </span>
                  </div>
                </div>
              )}

              {formData.pricing_type === 'hourly' && (
                <div>
                  <label className="block text-sm font-medium text-[#0A1929] mb-2">
                    Timepris (NOK) *
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      name="hourly_rate"
                      value={formData.hourly_rate}
                      onChange={handleChange}
                      min="0"
                      className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent"
                      placeholder="0"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 font-medium">
                      kr/time
                    </span>
                  </div>
                </div>
              )}
            </div>
          );

        case 4:
          return (
            <div className="space-y-6">
              <div>
                <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-2">
                  Hvor tilbyr du tjenesten?
                </h2>
                <p className="text-slate-500 text-sm mb-6">
                  Velg området der du kan utføre tjenesten
                </p>
              </div>

              {/* Bruk min posisjon */}
              <button
                type="button"
                onClick={handleUseLocation}
                disabled={locating}
                className={`w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-all ${
                  locating
                    ? 'bg-slate-100 text-slate-400'
                    : 'bg-gradient-to-r from-[#1E3A5F] to-[#0A1929] text-white shadow-md hover:shadow-lg'
                }`}
              >
                <span className={`material-symbols-outlined ${locating ? 'animate-pulse' : ''}`}>
                  {locating ? 'location_searching' : 'my_location'}
                </span>
                {locating ? 'Finner posisjon...' : 'Bruk min posisjon'}
              </button>

              {/* Fylke */}
              <div>
                <label className="block text-sm font-medium text-[#0A1929] mb-2">
                  Fylke *
                </label>
                <select
                  name="location_fylke"
                  value={formData.location_fylke}
                  onChange={(e) => setFormData({ location_fylke: e.target.value, location_city: '' })}
                  className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent appearance-none bg-white"
                >
                  <option value="">Velg fylke</option>
                  {FYLKER_MED_BYER.map(fylke => (
                    <option key={fylke.id} value={fylke.id}>{fylke.name}</option>
                  ))}
                </select>
              </div>

              {/* By */}
              {formData.location_fylke && byer.length > 1 && (
                <div>
                  <label className="block text-sm font-medium text-[#0A1929] mb-2">
                    By <span className="text-slate-400 font-normal">(valgfritt)</span>
                  </label>
                  <select
                    name="location_city"
                    value={formData.location_city}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1E3A5F] focus:border-transparent appearance-none bg-white"
                  >
                    <option value="">Hele fylket</option>
                    {byer.map(by => (
                      <option key={by.id} value={by.id}>{by.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {formData.location_fylke && (
                <div className="flex items-center gap-2 p-3 bg-green-50 rounded-lg text-green-700 text-sm">
                  <span className="material-symbols-outlined">check_circle</span>
                  <span>
                    {formData.location_city 
                      ? `${getByName(formData.location_city)}, ${getFylkeName(formData.location_fylke)}`
                      : getFylkeName(formData.location_fylke)
                    }
                  </span>
                </div>
              )}
            </div>
          );

        case 5:
          return renderImageStep();

        case 6:
          return renderPreviewStep();

        default:
          return null;
      }
    }

    return null;
  };

  const renderImageStep = () => (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-2">
          Legg til bilder
        </h2>
        <p className="text-slate-500 text-sm mb-6">
          {formData.job_type === 'tjeneste' 
            ? 'Vis frem deg selv, utstyret ditt eller tidligere arbeid'
            : 'Bilder hjelper folk å forstå oppdraget bedre'
          }
        </p>
      </div>

      {/* Image Preview */}
      {images.length > 0 && (
        <div className="grid grid-cols-3 md:grid-cols-5 gap-3 mb-4">
          {images.map((img, i) => (
            <div key={i} className="relative group">
              <div 
                onClick={() => setPrimaryImageIndex(i)}
                className={`cursor-pointer rounded-lg overflow-hidden aspect-square ${
                  i === primaryImageIndex 
                    ? 'ring-2 ring-[#1E3A5F] ring-offset-2' 
                    : 'hover:ring-2 hover:ring-slate-300'
                }`}
              >
                <img 
                  src={img.preview} 
                  alt=""
                  className="w-full h-full object-cover"
                />
                {i === primaryImageIndex && (
                  <div className="absolute top-1 left-1 bg-[#1E3A5F] text-white text-xs px-2 py-0.5 rounded-full flex items-center gap-1">
                    <span className="material-symbols-outlined text-xs">star</span>
                    Hoved
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeImage(i);
                }}
                className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
              >
                <span className="material-symbols-outlined text-sm">close</span>
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Upload Button */}
      {images.length < 5 && (
        <label className="flex flex-col items-center gap-3 p-8 border-2 border-dashed border-slate-200 rounded-xl cursor-pointer hover:border-[#1E3A5F] hover:bg-slate-50 transition-all">
          <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center">
            <span className="material-symbols-outlined text-3xl text-slate-400">add_photo_alternate</span>
          </div>
          <div className="text-center">
            <span className="text-[#0A1929] font-medium">Klikk for å laste opp bilder</span>
            <p className="text-xs text-slate-400 mt-1">(Maks 5 bilder, JPG/PNG)</p>
          </div>
          <input
            type="file"
            accept="image/*"
            multiple
            onChange={handleImageChange}
            className="hidden"
          />
        </label>
      )}

      <p className="text-sm text-slate-500 text-center">
        Du kan hoppe over dette steget hvis du ikke har bilder
      </p>
    </div>
  );

  const renderPreviewStep = () => (
    <div className="space-y-6">
      <div>
        <h2 className="font-heading font-semibold text-xl text-[#0A1929] mb-2">
          Klart for publisering!
        </h2>
        <p className="text-slate-500 text-sm mb-6">
          Se over informasjonen før du publiserer
        </p>
      </div>

      {/* Preview Card */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
        {/* Type badge */}
        <div className="px-4 pt-4">
          <span className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
            formData.job_type === 'tjeneste' 
              ? 'bg-purple-100 text-purple-700' 
              : 'bg-blue-100 text-blue-700'
          }`}>
            <span className="material-symbols-outlined text-sm">
              {formData.job_type === 'tjeneste' ? 'handyman' : 'assignment'}
            </span>
            {formData.job_type === 'tjeneste' ? 'Tjeneste' : 'Oppdrag'}
          </span>
        </div>

        {/* Image */}
        {images.length > 0 ? (
          <div className="aspect-video bg-slate-100 relative mt-3">
            <img 
              src={images[primaryImageIndex]?.preview} 
              alt="" 
              className="w-full h-full object-cover"
            />
            {images.length > 1 && (
              <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
                +{images.length - 1} bilder
              </div>
            )}
          </div>
        ) : (
          <div className="aspect-video bg-gradient-to-br from-[#1E3A5F] to-[#0A1929] flex items-center justify-center mt-3">
            <span className="material-symbols-outlined text-white/30 text-6xl">
              {formData.job_type === 'tjeneste' ? 'handyman' : 'work'}
            </span>
          </div>
        )}

        {/* Content */}
        <div className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 bg-slate-100 text-slate-600 text-xs px-2 py-1 rounded-full">
              <span className="material-symbols-outlined text-sm">
                {KATEGORIER.find(k => k.id === formData.category)?.icon}
              </span>
              {getCategoryName(formData.category)}
            </span>
          </div>
          <h3 className="font-heading font-semibold text-lg text-[#0A1929]">
            {formData.title || 'Ingen tittel'}
          </h3>
          <p className="text-slate-500 text-sm line-clamp-2">
            {formData.description || 'Ingen beskrivelse'}
          </p>
          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <div className="flex items-center gap-1 text-slate-500 text-sm">
              <span className="material-symbols-outlined text-lg">location_on</span>
              {formData.location_city 
                ? `${getByName(formData.location_city)}, ${getFylkeName(formData.location_fylke)}`
                : getFylkeName(formData.location_fylke) || 'Ikke valgt'
              }
            </div>
            <span className="font-heading font-bold text-lg text-[#1E3A5F]">
              {formData.pricing_type === 'hourly' 
                ? `${parseInt(formData.hourly_rate || 0).toLocaleString('nb-NO')} kr/t`
                : formData.pricing_type === 'negotiable'
                  ? 'Etter avtale'
                  : `${parseInt(formData.price || 0).toLocaleString('nb-NO')} kr`
              }
            </span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 pb-24">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-14 z-40">
        <div className="max-w-2xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => currentStep === 0 ? navigate('/') : prevStep()}
              className="flex items-center gap-1 text-[#1E3A5F] hover:text-[#0A1929] transition-colors p-2 -ml-2"
            >
              <span className="material-symbols-outlined">arrow_back</span>
              <span className="hidden md:inline text-sm">
                {currentStep === 0 ? 'Avbryt' : 'Tilbake'}
              </span>
            </button>
            <div className="text-center">
              <span className="text-xs text-slate-400">
                {formData.job_type && `Steg ${currentStep} av ${maxStep}`}
              </span>
              <h1 className="font-heading font-semibold text-[#0A1929]">
                {STEPS[currentStep]?.title || 'Ny annonse'}
              </h1>
            </div>
            <div className="w-16"></div>
          </div>

          {/* Progress Bar */}
          {formData.job_type && (
            <div className="flex gap-1 mt-3">
              {STEPS.map((step) => (
                <div
                  key={step.id}
                  className={`flex-1 h-1 rounded-full transition-colors ${
                    step.id <= currentStep ? 'bg-[#1E3A5F]' : 'bg-slate-200'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        <div className="bg-white rounded-2xl shadow-sm p-6 md:p-8">
          {error && (
            <div className="bg-red-50 text-red-600 px-4 py-3 rounded-lg mb-6 flex items-center gap-2">
              <span className="material-symbols-outlined">error</span>
              {error}
            </div>
          )}

          {renderStepContent()}
        </div>
      </div>

      {/* Fixed Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 p-4 pb-safe z-50">
        <div className="max-w-2xl mx-auto flex gap-3">
          {currentStep > 0 && (
            <button
              type="button"
              onClick={prevStep}
              className="flex-1 bg-slate-100 text-[#0A1929] py-4 rounded-xl font-medium hover:bg-slate-200 transition-colors flex items-center justify-center gap-2 text-base"
            >
              <span className="material-symbols-outlined">arrow_back</span>
              <span className="hidden sm:inline">Tilbake</span>
              <span className="sm:hidden">Tilbake</span>
            </button>
          )}
          
          {currentStep < maxStep ? (
            <button
              type="button"
              onClick={nextStep}
              className="flex-1 bg-[#0A1929] text-white py-4 rounded-xl font-medium hover:bg-[#1E3A5F] transition-colors flex items-center justify-center gap-2 shadow-lg text-base"
            >
              <span className="sm:hidden">{currentStep === maxStep - 1 ? 'Forhåndsvis' : 'Neste'}</span>
              <span className="hidden sm:inline">{currentStep === maxStep - 1 ? 'Forhåndsvis' : 'Neste'}</span>
              <span className="material-symbols-outlined">arrow_forward</span>
            </button>
          ) : (
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 bg-gradient-to-r from-[#1E3A5F] to-[#0A1929] text-white py-4 rounded-xl font-medium hover:opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg"
            >
              {loading ? (
                <>
                  <span className="animate-spin material-symbols-outlined">progress_activity</span>
                  Publiserer...
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined">publish</span>
                  Publiser annonse
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateJobPage;
